import React, { useState, useEffect, useRef } from "react";
import { Check, X } from "lucide-react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import BlurMessageOverlay from "../../careerPath/BlurMessageOverlay";

// Updated PricingOne component that integrates with the improved subscription flow
const PricingOne = () => {
  const [activeTab, setActiveTab] = useState("path");
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showOverLay, setShowOverLay] = useState(false);
  const featuresElementRef = useRef(null);
  const headerElementRef = useRef(null);
  const pricingContainerRef = useRef(null);
  const [headerWidth, setHeaderWidth] = useState(0);
  const navigate = useNavigate();

  // Stripe price IDs for each plan
  const stripePriceIds = {
    "FREE MVP TRIAL": "price_1RGJ6xQ2THmJYdbgTTsH0klQ", // Free trial doesn't need a Stripe ID
    "STANDARD PLAN": "price_1RGJ6cQ2THmJYdbgucRnB46i",
    "PREMIUM PLAN": "price_1OyZh5D0qM1GCZ0OdPExH3Z8",
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (isMobile) return;

      const headerElement = headerElementRef.current;
      const featuresElement = featuresElementRef.current;
      const pricingContainer = pricingContainerRef.current;

      if (!headerElement || !featuresElement || !pricingContainer) return;

      const headerRect = headerElement.getBoundingClientRect();
      const featuresRect = featuresElement.getBoundingClientRect();
      const containerRect = pricingContainer.getBoundingClientRect();

      const headerHeight = headerRect.height;
      const featuresTop = featuresRect.top;
      const containerBottom = containerRect.bottom;

      // Calculate the point where we should start showing the sticky header
      if (featuresTop < 0 && containerBottom > headerHeight + 100) {
        setIsHeaderSticky(true);
        if (headerWidth === 0) {
          setHeaderWidth(featuresElement.offsetWidth);
        }
      } else {
        setIsHeaderSticky(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    const handleResize = () => {
      const featuresElement = featuresElementRef.current;
      if (featuresElement) {
        setHeaderWidth(featuresElement.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);

    // Initial calculations
    handleScroll();
    handleResize();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [headerWidth, isMobile]);

  const features = [
    "Career Dashboard",
    "Mentor Connect",
    "Premium Courses",
    "Career Path",
    "Resume Builder Tool",
    "Interview Analysis",
    "Assessment Tracker",
    "Premium Webinars-Events",
  ];

  const plans = {
    launch: [
      {
        name: "FREE MVP TRIAL",
        price: "$0",
        duration: "7 days",
        features: {
          "Career Dashboard": true,
          "Mentor Connect": true,
          "Premium Courses": true,
          "Career Path": true,
        },
      },
      {
        name: "STANDARD PLAN",
        price: "$21",
        originalPrice: "$50",
        duration: "1 month",
        features: {
          "Career Dashboard": true,
          "Mentor Connect": true,
          "Premium Courses": true,
          "Career Path": true,
          "Resume Builder Tool": true,
          "Interview Analysis": true,
          "Assessment Tracker": true,
          "Premium Webinars-Events": true,
        },
      },
      {
        name: "CORPORATE PLAN",
        price: "Let's Discuss",
        originalPrice: "",
        duration: "Custom",
        features: {
          "Career Dashboard": true,
          "Mentor Connect": true,
          "Premium Courses": true,
          "Career Path": true,
          "Resume Builder Tool": true,
          "Interview Analysis": true,
          "Assessment Tracker": true,
          "Premium Webinars-Events": true,
        },
      },
    ],
    path: [
      {
        name: "FREE MVP TRIAL",
        price: "$0",
        duration: "7 days",
        features: {
          "Career Dashboard": true,
          "Mentor Connect": true,
          "Premium Courses": true,
          "Career Path": true,
        },
      },
      {
        name: "STANDARD PLAN",
        price: "$21",
        originalPrice: "$50",
        duration: "1 month",
        features: {
          "Career Dashboard": true,
          "Mentor Connect": true,
          "Premium Courses": true,
          "Career Path": true,
          "Resume Builder Tool": true,
          "Interview Analysis": true,
          "Assessment Tracker": true,
          "Premium Webinars-Events": true,
        },
      },
      {
        name: "CORPORATE PLAN",
        price: "Let's Discuss",
        originalPrice: "",
        duration: "Custom",
        features: {
          "Career Dashboard": true,
          "Mentor Connect": true,
          "Premium Courses": true,
          "Career Path": true,
          "Resume Builder Tool": true,
          "Interview Analysis": true,
          "Assessment Tracker": true,
          "Premium Webinars-Events": true,
        },
      },
    ],
  };

  // const subtitles = {
  //   launch: "Perfect for students and fresh graduates",
  //   path: " Ideal for working professionals",
  // };

  const titles = {
    launch: "Career Launch Plans",
    path: "Career Path Plans",
  };

  // Updated handleNavigate function - redirects to signup with plan details
  const handleNavigate = (planName) => {
    console.log(`Selected plan: "${planName}"`, "Active Tab:", activeTab);

    // Ensure we're using the exact plan name as defined in SUBSCRIPTION_PLANS
    const exactPlanNames = {
      "FREE MVP TRIAL": "FREE MVP TRIAL",
      "STANDARD PLAN": "STANDARD PLAN",
      "PREMIUM PLAN": "CORPORATE PLAN",
    };

    // Use the exact plan name or default to the provided one
    const exactPlanName = exactPlanNames[planName] || planName;

    // Get plan details
    const planList = plans[activeTab];
    const selectedPlanDetails = planList.find((plan) => plan.name === planName);

    if (!selectedPlanDetails) {
      console.error("Could not find selected plan details:", planName);
      return;
    }

    // Prepare plan data for signup
    const planData = {
      name: exactPlanName,
      price: selectedPlanDetails.price,
      duration: selectedPlanDetails.duration,
      careerLaunch: activeTab === "launch",
      priceId: stripePriceIds[exactPlanName],
      features: Object.keys(selectedPlanDetails.features).filter(
        (key) => selectedPlanDetails.features[key]
      ),
    };

    // Navigate to signup with plan data
    if (planName === "CORPORATE PLAN") {
      navigate("/price-discussion");
    } else {
      navigate("/signup", {
        state: {
          careerLaunch: activeTab === "launch",
          selectedPlan: planData,
        },
      });
    }
  };

  // Mobile card view for each plan
  const renderMobileView = () => {
    return (
      <div className="mobile-plans-container">
        <div className="text-center mb-4">
          <h3 className="fw-bold">{titles[activeTab]}</h3>
        </div>

        {plans[activeTab].map((plan, index) => (
          <div key={index} className="mobile-plan-card">
            <div className="mobile-plan-header">
              <h4>{plan.name}</h4>
              <div className="price">
                <span className="current-price">{plan.price}</span>
                {plan.originalPrice && (
                  <span className="original-price">{plan.originalPrice}</span>
                )}
              </div>
              <div className="duration">{plan.duration}</div>
              <button
                className="btn btn-primary try-free-btn get-started-btn"
                onClick={() => {
                  handleNavigate(plan.name);
                }}
              >
                Get Started
              </button>
            </div>

            <div className="mobile-plan-features">
              {features.map((feature, featureIndex) =>
                plan.features[feature] ? (
                  <div key={featureIndex} className="mobile-feature-item">
                    <Check size={18} className="check-icon me-2" />
                    <span>{feature}</span>
                  </div>
                ) : null
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Desktop table view
  const renderDesktopView = () => {
    return (
      <div className="pricing-table-container" ref={featuresElementRef}>
        <div
          className={`headers-row ${isHeaderSticky ? "sticky" : ""}`}
          ref={headerElementRef}
          style={isHeaderSticky ? { width: headerWidth + "px" } : {}}
        >
          <div className="feature-column">
            <div
              className="feature-list-header"
              style={{ backgroundColor: "white" }}
            >
              {/* <h3 className="fw-bold">{titles[activeTab]}</h3> */}
              <img src="./images/logo.png" style={{ borderRadius: "14px" }} />
            </div>
          </div>
          {plans[activeTab].map((plan, index) => (
            <div key={index} className="plan-column">
              <div className="plan-header">
                <h4>{plan.name}</h4>
                <div className="price">
                  <span className="current-price">{plan.price}</span>
                  {plan.originalPrice && (
                    <span className="original-price">{plan.originalPrice}</span>
                  )}
                </div>
                <div className="duration">{plan.duration}</div>
                <button
                  className="btn btn-primary try-free-btn get-started-btn-bottom"
                  onClick={() => {
                    handleNavigate(plan.name);
                  }}
                >
                  Get Started
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="features-and-plans">
          <div className="feature-column">
            <div className="feature-list" style={{ backgroundColor: "white" }}>
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="feature-item justify-content-center fw-bold"
                >
                  {feature}
                </div>
              ))}
            </div>
          </div>

          {plans[activeTab].map((plan, index) => (
            <div key={index} className="plan-column">
              <div className="plan-features">
                {features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="feature-availability">
                    {plan.features[feature] === true ? (
                      <Check size={20} className="check-icon" />
                    ) : (
                      <X size={20} className="x-icon" />
                    )}
                  </div>
                ))}
              </div>
              {/* <div className="plan-footer">
                <button
                  className="btn btn-primary try-free-btn get-started-btn-bottom"
                  onClick={() => {
                    handleNavigate(plan.name);
                  }}
                >
                  Get Started
                </button>
              </div> */}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const [hoverText, setHoverText] = useState("");
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({
    left: 0,
    top: 0,
  });

  const handleMouseEnter = (text) => {
    setHoverText(text);
    setTooltipVisible(true);
  };

  const handleMouseMove = (e) => {
    const rect = e.target.getBoundingClientRect();
    setTooltipPosition({
      left: rect.left - 160 + rect.width / 2,
      top: rect.top - 375 + window.scrollY,
    });
  };

  const handleMouseLeave = () => {
    setTooltipVisible(false);
    setHoverText("");
  };

  return (
    <>
      {showOverLay && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            backdropFilter: "blur(5px)",
            justifyContent: "center",
            zIndex: 999,
          }}
        >
          <button
            className="btn btn-primary"
            style={{
              position: "absolute",
              zIndex: 1000,
              top: 158,
              left: 470,
            }}
            onClick={() => {
              setShowOverLay(false);
            }}
          >
            X
          </button>
          <BlurMessageOverlay />
        </div>
      )}
      <div className="pricing-container" ref={pricingContainerRef}>
        <div className="text-center my-5">
          {!isMobile && tooltipVisible && (
            <div
              style={{
                position: "absolute",
                top: `${tooltipPosition.top + 10}px`,
                left: `${tooltipPosition.left}px`,
                transform: "translateX(-50%)",
                backgroundColor: "#6600CC",
                color: "#fff",
                borderRadius: "8px",
                zIndex: 1000,
                padding: "10px 14px",
                maxWidth: "450px",
                fontSize: "18px",
                textAlign: "center",
                backdropFilter: "blur(5px)",
                boxShadow: "0 4px 15px rgba(0,0,0,0.25)",
                transition: "all 0.3s ease",
                whiteSpace: "nowrap",
              }}
            >
              {hoverText}
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 0,
                  height: 0,
                  borderLeft: "8px solid transparent",
                  borderRight: "8px solid transparent",
                  borderTop: "8px solid #6600CC",
                }}
              />
            </div>
          )}
          <div className="goal-buttons">
            <button
              className={`btn ${activeTab === "launch" ? "active-goal" : ""}`}
              onClick={() => setActiveTab("launch")}
              // onMouseOver={(e) => {
              //   e.target.style.background = "#006600";
              //   e.target.style.color = "white";
              //   e.target.style.boxShadow = "0 3px 6px rgba(108, 92, 231, 0.4)";
              // }}
              // onMouseOut={(e) => {
              //   e.target.style.background = "white";
              //   e.target.style.color = "black";
              //   e.target.style.boxShadow = "0 2px 4px rgba(108, 92, 231, 0.3)";
              // }} 
              onMouseEnter={() =>
                handleMouseEnter("Perfect for students and fresh graduates")
              }
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              Career Launch
            </button>
            <button
              className={`btn ${activeTab === "path" ? "active-goal " : ""}`}
              onClick={() => setActiveTab("path")}
              // onMouseOver={(e) => {
              //   e.target.style.background = "#006600";
              //   e.target.style.boxShadow = "0 3px 6px rgba(108, 92, 231, 0.4)";
              // }}
              // onMouseOut={(e) => {
              //   e.target.style.background = "#6600CC";
              //   e.target.style.color = "white";
              //   e.target.style.boxShadow = "0 2px 4px rgba(108, 92, 231, 0.3)";
              // }}
              onMouseEnter={() =>
                handleMouseEnter("Ideal for working professionals")
              }
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              Career Growth
            </button>
          </div>
          {/* <p className="goal-subtitle">{subtitles[activeTab]}</p> */}
        </div>

        {isMobile ? renderMobileView() : renderDesktopView()}

        {/* Spacer element to ensure smooth transition of sticky header */}
        {isHeaderSticky && <div className="sticky-spacer"></div>}
      </div>

      <style>
        {`
                    .pricing-container {
                        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
                        max-width: 1200px;
                        margin: 0 auto;
                        color: #2d3748;
                        position: relative;
                        padding: 0 15px;
                        overflow: visible;
                    }

                    .goal-buttons {
                        display: flex;
                        justify-content: center;
                        gap: 1rem;
                        margin: 1.5rem 0;
                        flex-wrap: wrap;
                    }

                    .goal-buttons .btn {
                        min-width: 140px;
                        border: 2px solid #6600cc;
                        color: #6600cc;
                        background-color: white;
                        padding: 0.75rem 1rem;
                        border-radius: 8px;
                        font-size : 16px;
                        font-weight: 600;
                        transition: all 0.3s ease;
                        box-shadow: 0 2px 10px rgba(102, 0, 204, 0.05);
                        margin-bottom: 0.5rem;
                    }

                    .goal-buttons .btn:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 4px 12px rgba(102, 0, 204, 0.1);
                    }

                    .goal-buttons .btn.active-goal {
                        background-color: #006600;
                        color: white;
                        box-shadow: 0 4px 12px rgba(102, 0, 204, 0.2);
                    }

                    .goal-subtitle {
                        color: #4a5568;
                        margin: 1rem 0 2rem;
                        font-size: 1.1rem;
                        font-weight: 500;
                    }

                    /* Desktop Styles */
                    .pricing-table-container {
                        position: relative;
                        overflow: visible;
                    }
                    
                    .headers-row {
                        display: flex;
                        width: 100%;
                        background-color: #fff;
                        z-index: 100;
                    }
                    
                    /* Modified sticky header style - completely removed all animation and transform properties */
                    .headers-row.sticky {
                        position: fixed;
                        top: 75px;
                        left: 0;
                        right: 0;
                        margin: 0 auto;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                        z-index: 100;
                        transform: none !important;
                        transition: none !important;
                        animation: none !important;
                        padding-top: 0px;
                    }
                    
                    /* Make sure the spacer has the exact same height as the header to prevent content jump */
                    .sticky-spacer {
                        height: 280px;
                        width: 100%;
                    }
                    
                    .features-and-plans {
                        display: flex;
                        width: 100%;
                        margin-top: 0;
                    }
                    
                    .feature-column, .plan-column {
                        flex: 1;
                    }
                    
                    .feature-list-header, .plan-header {
                        height: 260px;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        padding: 0.7rem;
                        min-height: 260px;
                    }

                    .feature-list-header {
                        background-color: #f0f0ff;
                        border-radius: 12px 12px 0 0;
                    }

                    .feature-list-header h3 {
                        color: #6600cc;
                        margin: 0;
                        font-weight: 700;
                        font-size: 2rem;
                        padding-top: 10px;
                    }

                    .feature-list {
                        background-color: #f0f0ff;
                        padding: 0.5rem;
                        border-radius: 0 0 12px 12px;
                    }

                    .feature-item {
                        padding: 0.85rem 0;
                        border-bottom: 1px solid rgba(102, 0, 204, 0.1);
                        color: #2d3748;
                        font-size: 0.95rem;
                        height: 52px;
                        display: flex;
                        align-items: center;
                    }

                    .plan-header {
                        text-align: center;
                        background-color: white;
                        position: relative;
                        border: 1px solid #e2e8f0;
                        border-bottom: none;
                        border-radius: 12px 12px 0 0;
                    }

                    .plan-header h4 {
                        color: #6600cc;
                        font-weight: 700;
                        margin-bottom: 1rem;
                        letter-spacing: 0.5px;
                        font-size: 1.1rem;
                    }

                    .price {
                        margin: 1.2rem 0;
                        display: flex;
                        justify-content: center;
                        align-items: baseline;
                        flex-wrap: wrap;
                    }

                    .current-price {
                        font-size: 2.2rem;
                        font-weight: 700;
                        color: #2d3748;
                    }

                    .original-price {
                        font-size: 1.1rem;
                        color: #a0aec0;
                        text-decoration: line-through;
                        margin-left: 0.5rem;
                    }

                    .duration {
                        color: #4a5568;
                        margin: 0.7rem 0 1.5rem;
                        font-size: 0.95rem;
                    }

                    .get-started-btn-bottom {
                        color: white;
                        background-color: #6600cc;
                        border: none;
                        padding: 0.75rem 2rem;
                        border-radius: 8px;
                        font-weight: 600;
                        transition: all 0.3s ease;
                        box-shadow: 0 4px 12px rgba(102, 0, 204, 0.2);
                        letter-spacing: 0.5px;
                        width: 80%;
                        margin: 0 auto;
                    }

                    .plan-features {
                        display: flex;
                        flex-direction: column;
                        border: 1px solid #e2e8f0;
                        border-top: none;
                        border-bottom: none;
                        background-color: white;
                        min-height: calc(100% - 105px);
                    }

                    .feature-availability {
                        padding: 2rem 0.5rem;
                        border-bottom: 1px solid rgba(0, 0, 0, 0.04);
                        text-align: center;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        height: 52px;
                        font-size: 0.95rem;
                        overflow: visible;
                    }

                    .check-icon {
                        color: #006600;
                    }

                    .x-icon {
                        color: #e53e3e;
                        opacity: 0.6;
                    }

                    .feature-value {
                        font-weight: 600;
                        color: #6600cc;
                        background-color: rgba(102, 0, 204, 0.08);
                        padding: 0.2rem 0.6rem;
                        border-radius: 4px;
                        font-size: 0.9rem;
                    }

                    .plan-footer {
                        padding: 1.5rem;
                        text-align: center;
                        background-color: #fafafa;
                        border: 1px solid #e2e8f0;
                        border-top: none;
                        border-radius: 0 0 12px 12px;
                    }

                    /* Mobile View Styles */
                    .mobile-plans-container {
                        padding: 0 0.5rem;
                    }

                    .mobile-plan-card {
                        margin-bottom: 2rem;
                        border-radius: 12px;
                        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
                        overflow: hidden;
                    }

                    .mobile-plan-header {
                        background-color: white;
                        padding: 1.5rem;
                        text-align: center;
                        border-bottom: 1px solid rgba(102, 0, 204, 0.1);
                    }

                    .mobile-plan-header h4 {
                        color: #6600cc;
                        font-weight: 700;
                        font-size: 1.2rem;
                        margin-bottom: 1rem;
                    }

                    .mobile-plan-features {
                        padding: 1rem 1.5rem;
                        background-color: #fafafa;
                    }

                    .mobile-feature-item {
                        display: flex;
                        align-items: center;
                        padding: 0.75rem 0;
                        border-bottom: 1px solid rgba(0, 0, 0, 0.04);
                        font-size: 0.9rem;
                    }

                    .mobile-feature-item:last-child {
                        border-bottom: none;
                    }

                    @media (max-width: 767px) {
                        .goal-buttons .btn {
                            min-width: 120px;
                            padding: 0.6rem 1rem;
                            font-size: 0.9rem;
                        }

                        .goal-subtitle {
                            font-size: 1rem;
                            margin-bottom: 1.5rem;
                        }

                        .current-price {
                            font-size: 1.8rem;
                        }

                        .original-price {
                            font-size: 1rem;
                        }

                        .duration {
                            margin: 0.5rem 0 1.2rem;
                        }

                        .get-started-btn {
                            width: 100%;
                            padding: 0.6rem 1rem;
                        }
                    }

                    @media (min-width: 768px) and (max-width: 991px) {
                        .headers-row.sticky {
                            top: 10px;
                        }

                        .feature-list-header h3 {
                            font-size: 1.7rem;
                        }

                        .plan-header h4 {
                            font-size: 1rem;
                        }

                        .feature-item, .feature-availability {
                            font-size: 0.85rem;
                            padding: 0.6rem 0.3rem;
                            height: auto;
                            min-height: 48px;
                        }

                        .current-price {
                            font-size: 1.8rem;
                        }
                    }
                `}
      </style>
    </>
  );
};

export default PricingOne;
